import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface OnboardingMessage {
  message: string;
  step: string;
  context?: Record<string, any>;
}

export interface OnboardingResponse {
  response: string;
  extracted_data?: Record<string, any>;
  suggestions: string[];
  next_step?: string;
  completion_percentage: number;
  validation_errors?: string[];
}

export interface OnboardingProgress {
  user_id: string;
  current_step: string;
  completed_steps: string[];
  completion_percentage: number;
  data_collected: Record<string, any>;
  missing_critical: string[];
}

export interface BulkImportRequest {
  data_type: string;
  data: Record<string, any>[];
}

export const smartOnboardingApi = {
  /**
   * Send a message in the onboarding conversation
   */
  async sendMessage(message: OnboardingMessage): Promise<OnboardingResponse> {
    const response = await api.post('/smart-onboarding/message', message);
    return response.data;
  },

  /**
   * Get user's onboarding progress
   */
  async getProgress(): Promise<OnboardingProgress> {
    const response = await api.get('/smart-onboarding/progress');
    return response.data;
  },

  /**
   * Bulk import data from CSV/JSON
   */
  async bulkImport(importRequest: BulkImportRequest): Promise<any> {
    const response = await api.post('/smart-onboarding/bulk-import', importRequest);
    return response.data;
  },

  /**
   * Skip an onboarding step
   */
  async skipStep(step: string): Promise<any> {
    const response = await api.post('/smart-onboarding/skip-step', { step });
    return response.data;
  },

  /**
   * Mark onboarding as complete
   */
  async complete(data?: Record<string, any>): Promise<any> {
    const response = await api.post('/smart-onboarding/complete', { onboarding_data: data });
    return response.data;
  },

  /**
   * Parse CSV file for bulk import
   */
  parseCSV(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim());
          
          const data = lines.slice(1)
            .filter(line => line.trim())
            .map(line => {
              const values = line.split(',').map(v => v.trim());
              const obj: Record<string, any> = {};
              headers.forEach((header, index) => {
                obj[header] = values[index];
              });
              return obj;
            });
          
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }
};
